import '@mdi/font/css/materialdesignicons.css'
import './assets/styles.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import router from './router'
// eslint-disable-next-line no-unused-vars
import { i18n } from './i18n/plugin'

import { initFirebase } from './firebase/app'
import { initAuthErrorHandler } from './utils/authErrorHandler'
import { setupVueErrorHandler } from './utils/vueErrorHandler'
import { initChunkLoadHandler, setAppUpdateStore, isChunkLoadError } from './utils/chunkLoadHandler'
import { useAppUpdateStore } from './stores/appUpdate'
import * as Sentry from '@sentry/vue'
import { createSentryPiniaPlugin } from '@sentry/vue'

initFirebase()

// Handle chunk load failures when app is updated during an active session
initChunkLoadHandler()

// Initialize global auth error handling
initAuthErrorHandler()

// Vuetify
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import { aliases, mdi } from 'vuetify/iconsets/mdi'

const vuetify = createVuetify({
  theme: {
    defaultTheme: 'modern',
    themes: {
      modern: {
        dark: false,
        colors: {
          primary: '#1976D2', // Modern blue
          secondary: '#424242', // Neutral dark grey
          accent: '#FF4081', // Vibrant pink
          background: '#F5F7FA', // Light background
          surface: '#FFFFFF',
          error: '#FF5252',
          info: '#2196F3',
          success: '#4CAF50',
          warning: '#FFC107'
        }
      }
    }
  },
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: {
      mdi
    }
  }
})

const vueApp = createApp(App)

const pinia = createPinia()
pinia.use(createSentryPiniaPlugin())
vueApp.use(pinia)
vueApp.use(router)
vueApp.use(i18n)
vueApp.use(vuetify)

// Initialize Sentry error reporting
// Only enabled in production by default (can be overridden with VITE_REPORT_ERRORS env var)
const shouldReport =
  import.meta.env.VITE_REPORT_ERRORS !== undefined
    ? import.meta.env.VITE_REPORT_ERRORS === 'true'
    : import.meta.env.PROD

if (shouldReport) {
  Sentry.init({
    app: vueApp,
    dsn: 'https://274314a4e9e74fe5526fff3e3eb5514a@o4510745755844608.ingest.us.sentry.io/4510745758203904',
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    // Send default PII data (e.g., automatic IP address collection)
    sendDefaultPii: true,
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    // Environment and release info
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || 'dev',
    // Filter out handled errors
    beforeSend(event, hint) {
      const error = hint.originalException
      // Don't report auth errors (401) - handled by authErrorHandler
      if (error?.response?.status === 401) {
        return null
      }
      // Don't report chunk load errors - handled by chunkLoadHandler
      if (isChunkLoadError(error)) {
        return null
      }
      // Don't report Firebase IndexedDB connection loss — browser-level issue (background tab, low memory, etc.)
      if (error?.message?.includes('Connection to Indexed Database server lost')) {
        return null
      }
      return event
    }
  })

  console.log('[Error Reporting] Sentry initialized')
} else {
  console.log('[Error Reporting] Disabled in development mode')
}

// Set up additional Vue error handlers for custom logging
setupVueErrorHandler(vueApp)

vueApp.mount('#app')

// Connect chunk load handler to the store after Pinia is initialized
setAppUpdateStore(useAppUpdateStore())
